<template>
  <div class="asc-custom-analytics-performance-wrapper">
    <asc-card class="asc-custom-analytics-performance--card" flat>
      <template #title>Mx Recommendation Performance</template>
      <asc-divider />
      <v-row class="text-center">
        <v-col class="precision">
          <div class="precision-header">
            PRECISION
            <span>
              <v-tooltip top content-class="precision-tooltip" color="black">
                <template #activator="{ on, attrs }">
                  <v-icon class="icon-info" :size="15" v-bind="attrs" v-on="on">
                    mdi-information
                  </v-icon>
                </template>
                <span class="precision-tooltip">
                  <strong
                    >Precision is the percentage of successfully predicted Mx
                    Recommendations over the total Mx Recommendations for this
                    analytic.</strong
                  >
                  <div class="precision-equation">
                    Precision =
                    <div class="fraction">
                      <span class="numerator">Successful Mx Recs</span>
                      <span class="denominator"
                        >Successful + Unsuccessful Mx Recs</span
                      >
                    </div>
                  </div>
                </span>
              </v-tooltip>
            </span>
          </div>
        </v-col>
      </v-row>
      <v-row class="text-center">
        <v-col>
          <div v-if="alertActive" class="precision-value">
            {{ getPrecisionPercentage }}
          </div>
          <div v-if="!alertActive" class="precision-value-inactive">
            Activate alert to monitor its recommendation performance.
          </div>
        </v-col>
      </v-row>
      <v-row class="text-center">
        <v-col class="precision-date">
          <div>{{ getPerformanceSetDate }}</div>
        </v-col>
      </v-row>
      <v-row class="text-center">
        <v-col>
          <asc-tooltip
            :tooltip="isDataBricksRunning ? tooltipMsg : ''"
            :tooltip-length="100"
            :left="true"
          >
            <asc-checkbox
              v-model="resetPerformance"
              label="Reset performance values when updating logic."
              :disabled="isDataBricksRunning || isReadOnly"
            />
          </asc-tooltip>
        </v-col>
      </v-row>
      <asc-divider />
      <v-row class="results">
        <v-col class="successful" :cols="7">
          <div class="result-header">
            SUCCESSFUL
            <span>
              <v-tooltip top content-class="precision-tooltip" color="black">
                <template #activator="{ on, attrs }">
                  <v-icon class="icon-info" :size="15" v-bind="attrs" v-on="on">
                    mdi-information
                  </v-icon>
                </template>
                <span class="precision-tooltip">
                  <strong
                    >Count of Maintenance Recommendations that successfully
                    predicted maintenance.</strong
                  >
                  <div class="precision-tooltip--subtext">
                    Count of Mx Recs with "Closed (Mx Complete)" status + Count
                    of Mx Recs with "Part Failed Before Replacement" status
                  </div>
                </span>
              </v-tooltip>
            </span>
          </div>
          <v-row class="result-subheader">
            <v-col class="vertical-border">
              <div class="result-item">
                <span class="result-title">Mx Complete</span>
                <span class="result-value">{{ getMxComplete }}</span>
              </div>
            </v-col>
            <v-col>
              <div class="result-item">
                <span class="result-title">Part Failed</span>
                <span class="result-value">{{ getPartFailed }}</span>
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="unsuccessful" :cols="5">
          <div class="result-header">
            UNSUCCESSFUL
            <span>
              <v-tooltip top content-class="precision-tooltip" color="black">
                <template #activator="{ on, attrs }">
                  <v-icon class="icon-info" :size="15" v-bind="attrs" v-on="on">
                    mdi-information
                  </v-icon>
                </template>
                <span class="precision-tooltip">
                  <strong
                    >Count of Maintenance Recommendations that did not
                    successfully predict maintenance.</strong
                  >
                  <div class="precision-tooltip--subtext">
                    Count of Mx Recs with "No Action Required" status
                  </div>
                </span>
              </v-tooltip>
            </span>
          </div>
          <v-row class="result-subheader">
            <v-col>
              <div class="result-item">
                <span class="result-title">No Action Required</span>
                <span class="result-value">{{ getNoActionRequired }}</span>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </asc-card>
  </div>
</template>

<script>
import AscCard from '../atoms/AscCard.vue'
import AscCheckbox from '../atoms/AscCheckbox'
import AscDivider from '@/components/atoms/AscDivider'
import { getFormattedUTCDate } from '@/utilities/dates'
import AscTooltip from '@/components/atoms/AscTooltip'

export default {
  name: 'AscCustomAnalyticsPerformanceWrapper',

  components: {
    AscCard,
    AscCheckbox,
    AscDivider,
    AscTooltip,
  },

  props: {
    tooltipMsg: {
      type: String,
      default: '',
    },
    isDataBricksRunning: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    performanceData: {
      type: Object,
      default: () => {},
    },
    performanceSetDate: {
      type: String,
      default: '',
    },
    resetPerformanceValues: {
      type: Boolean,
      default: false,
    },
    alertActive: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {}
  },

  computed: {
    getPrecisionPercentage() {
      return this.alertActive
        ? (this.performanceData.precision * 100).toFixed(0) + '%'
        : 'Activate alert to monitor its recommendation performance.'
    },
    getMxComplete() {
      return this.alertActive ? this.performanceData.mxComplete : '-'
    },
    getPartFailed() {
      return this.alertActive ? this.performanceData.partFailed : '-'
    },
    getNoActionRequired() {
      return this.alertActive ? this.performanceData.falsePositives : '-'
    },
    getPerformanceSetDate() {
      return this.performanceSetDate == null ||
        this.performanceSetDate == undefined ||
        this.performanceSetDate == ''
        ? ''
        : 'Performance metrics set on ' +
            getFormattedUTCDate(this.performanceSetDate)
    },
    resetPerformance: {
      get() {
        return this.resetPerformanceValues
      },
      set(val) {
        this.$emit('isDirty')
        this.$emit('handleChangeResetPerformanceValues', val)
      },
    },
  },
}
</script>

<style lang="scss">
.precision-tooltip {
  max-width: 350px;
  white-space: normal;

  .precision-tooltip--subtext {
    margin-top: 20px;
  }

  .precision-equation {
    display: flex;
    align-items: center;
    margin-top: 8px;
  }

  .fraction {
    display: inline-block;
    text-align: center;
    margin-left: 8px;
  }

  .numerator {
    border-bottom: 1px solid white;
    display: block;
    margin-bottom: 2px;
  }

  .denominator {
    display: block;
  }
}

.asc-custom-analytics-performance-wrapper {
  .asc-custom-analytics-performance--card {
    padding: 0px 0px 0px 0px;
  }

  .asc-card__text {
    padding: 0px;
  }

  .asc-divider {
    border-color: $gray-200;
  }

  .text-center {
    padding: 0px;
    margin: 0px;
  }

  .icon-info {
    display: inline-block;
    color: $accent-600;
    font-size: $font-size-lrg;
    padding-left: $spacing-sm;
  }

  .asc-icon {
    display: inline-block;
    top: -2px;
    padding-left: $spacing-sm;
    position: relative;
  }

  .asc-icon .icon-info {
    color: $accent-600;
    cursor: pointer;
    display: inline-block;
    font-size: $font-size-lrg;
  }

  .precision {
    padding: 0px;
    margin: 0px 4px 0px 4px;
  }

  .precision-header {
    background-color: $gray-0;
    padding: 8px 1px 8px 1px;
    margin-top: 10px;
    font-size: 12px;
  }

  .precision-value {
    font-size: 26px;
    margin: 16px 0px 0px 0px;
    color: $gray-900;
  }

  .precision-value-inactive {
    font-size: 14px;
    margin: 16px 0px 0px 0px;
  }

  .precision-date {
    font-size: 12px;
    color: $gray-800;
    margin: 0px;
    padding: 0px;
  }

  .results {
    margin: 0px 4px 0px 4px;
  }

  .successful {
    border-right: 1px solid $gray-200;
    padding: 0px 4px 0px 0px;
  }

  .unsuccessful {
    padding: 0px 4px 0px 4px;
  }

  .vertical-border {
    border-right: 1px solid $gray-200;
  }

  .result-header {
    text-align: center;
    background-color: $gray-0;
    padding: 8px 1px 8px 1px;
    font-size: 12px;
    margin: 6px 0px 0px 0px;
  }

  .result-subheader {
    margin: 0px;
  }

  .result-item {
    text-align: center;
    padding: 32px 0px 32px 0px;
  }

  .result-title {
    font-size: 12px;
    display: block;
    color: $gray-600;
  }

  .result-value {
    font-size: 14px;
    margin-top: 4px;
    display: block;
  }
}
</style>
